import db, { clearStores } from '../db/localdb';
import { syncAPI } from '../services/api';

const DEVICE_ID_KEY = 'shopbook_device_id';

function getDeviceId() {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) { id = crypto.randomUUID(); localStorage.setItem(DEVICE_ID_KEY, id); }
  return id;
}

function emit(syncing) {
  window.dispatchEvent(new CustomEvent('sb:syncing', { detail: { syncing } }));
}

class SyncManager {
  constructor() {
    this._interval     = null;
    this._onlineHandler = null;
    this._syncing      = false;
  }

  start() {
    if (this._interval) return;
    this._onlineHandler = () => this.sync();
    window.addEventListener('online', this._onlineHandler);
    this._interval = setInterval(() => this.sync(), 45_000);
    setTimeout(() => this.sync(), 800);
  }

  stop() {
    if (this._interval) { clearInterval(this._interval); this._interval = null; }
    if (this._onlineHandler) {
      window.removeEventListener('online', this._onlineHandler);
      this._onlineHandler = null;
    }
  }

  // Best-effort push of anything queued, e.g. before logging out. Resolves
  // with the number of ops still waiting afterwards.
  async flush() {
    if (navigator.onLine) {
      try { await db.open(); await this._push(); } catch { /* counted below */ }
    }
    return db.outbox.filter(op => !op.synced_at && !op.failed_at).count();
  }

  async sync() {
    if (!navigator.onLine || this._syncing) return;
    this._syncing = true;
    emit(true);
    try {
      // Ensure DB is fully open (no-op if already open; waits out any version upgrade)
      await db.open();
      await this._bootstrap();
      await this._push();
      await this._pull();
    } catch (err) {
      console.warn('[SyncManager] sync error:', err.message);
    } finally {
      this._syncing = false;
      emit(false);
    }
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────
  // Runs only once per device (until clearStores resets lastPull to null).
  async _bootstrap() {
    const meta = await db.meta.get('lastPull');
    if (meta?.value) return;

    try {
      const { data } = await syncAPI.bootstrap();
      if (!data.success) return;

      await clearStores();
      const { products, sales, purchases, customers, sale_payments, expenses, production_runs } = data.snapshot;
      await Promise.all([
        products?.length        && db.products.bulkPut(products),
        sales?.length           && db.sales.bulkPut(sales),
        purchases?.length       && db.purchases.bulkPut(purchases),
        customers?.length       && db.customers.bulkPut(customers),
        sale_payments?.length   && db.sale_payments.bulkPut(sale_payments),
        expenses?.length        && db.expenses.bulkPut(expenses),
        production_runs?.length && db.production_runs.bulkPut(production_runs),
      ].filter(Boolean));

      await db.meta.put({ key: 'lastPull', value: data.server_time });
      console.log('[SyncManager] Bootstrap complete —', products?.length, 'products,', sales?.length, 'sales');
    } catch (err) {
      console.warn('[SyncManager] Bootstrap failed:', err.message);
    }
  }

  // ── Push ───────────────────────────────────────────────────────────────────
  // Flushes all unsynced outbox ops to the server in one batch request.
  // Per-op results:
  //   applied  → synced
  //   rejected → the server refused it for good (bad data / no permission).
  //              Marked failed and shown to the user — it must never look saved.
  //   retry    → temporary server error; left pending for the next round.
  async _push() {
    const pending = await db.outbox.filter(op => !op.synced_at && !op.failed_at).toArray();
    if (!pending.length) return;

    const device_id = getDeviceId();
    const ops = pending.map(({ op_id, entity, op_type, payload }) => ({
      op_id, entity, op_type, payload,
    }));

    let data;
    try {
      ({ data } = await syncAPI.push({ ops, device_id }));
    } catch (err) {
      // Lapsed subscription (read-only) or frozen account: keep everything
      // queued — it will go through once the account is active again.
      if (err.response?.status === 402 || err.response?.status === 403) return;
      throw err;
    }
    if (!data.success) return;

    const now = new Date().toISOString();
    let failed = 0;
    for (const r of data.results) {
      if (r.result === 'applied' || r.result === 'duplicate') {
        await db.outbox.where('op_id').equals(r.op_id).modify({ synced_at: now });
      } else if (r.result === 'rejected' || r.result === 'conflict') {
        await db.outbox.where('op_id').equals(r.op_id).modify({
          failed_at: now, error: r.detail || r.conflict?.message || 'Rejected by the server',
        });
        failed++;
      }
    }
    if (failed) window.dispatchEvent(new CustomEvent('sb:sync-failed', { detail: { count: failed } }));

    // Reconcile Dexie stock for any products the server recalculated
    if (data.stock_updates?.length) {
      for (const u of data.stock_updates) {
        await db.products.where('id').equals(u.id).modify({
          stock_qty: u.stock_qty,
          oversold:  u.oversold,
        });
      }
    }

    const applied = data.results.filter(r => r.result === 'applied').length;
    if (applied) console.log(`[SyncManager] Pushed ${applied} ops`);
  }

  // ── Pull ───────────────────────────────────────────────────────────────────
  // Fetches records updated since lastPull and upserts them into Dexie.
  async _pull() {
    const meta  = await db.meta.get('lastPull');
    const since = meta?.value || new Date(0).toISOString();

    const { data } = await syncAPI.pull(since);
    if (!data.success) return;

    const { products, sales, purchases, customers, sale_payments, expenses, production_runs } = data.changes;
    await Promise.all([
      products?.length        && db.products.bulkPut(products),
      sales?.length           && db.sales.bulkPut(sales),
      purchases?.length       && db.purchases.bulkPut(purchases),
      customers?.length       && db.customers.bulkPut(customers),
      sale_payments?.length   && db.sale_payments.bulkPut(sale_payments),
      expenses?.length        && db.expenses.bulkPut(expenses),
      production_runs?.length && db.production_runs.bulkPut(production_runs),
    ].filter(Boolean));

    if (data.server_time) {
      await db.meta.put({ key: 'lastPull', value: data.server_time });
    }

    const total = (products?.length || 0) + (sales?.length || 0) +
                  (purchases?.length || 0) + (customers?.length || 0);
    if (total) console.log(`[SyncManager] Pulled ${total} updated records`);
  }
}

export default new SyncManager();
